/**
 * Corporation Resource Histogram — remaining resource load across every P6 copy
 * (fab shops + field), with the fab-shop capacity line. Formerly "Fabrication Histogram".
 * Route: /fabrication-histogram (Resource Management root item)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';
import { useToast } from '../../hooks/useToast';
import { ListenButton } from '../common/AudioGuide';
import FabricationHistogramChart, { seriesColor, stripDumpSuffix } from './FabricationHistogramChart';
import FabricationHistogramHelpModal, { FAB_HIST_GUIDE_ID } from './FabricationHistogramHelpModal';
import FabricationHistogramAuditModal from './FabricationHistogramAuditModal';
import FabricationHistogramPortfoliosModal from './FabricationHistogramPortfoliosModal';
import FabricationHistogramFilterPane from './FabricationHistogramFilterPane';
import FilterDropdown, { FilterRadioList } from '../Reports/FilterDropdown';
import {
  DEFAULT_WINDOW_HORIZON,
  defaultLaborTypes,
  defaultResourceDisciplines,
  histogramScopeChip,
  histogramTitle,
  isFabricationDisciplineSet,
  isP6LegendId,
  loadSlicerPanels,
  p6LegendFields,
  programsVisibleForPortfolios,
} from '../../utils/fabHistogramFilters';
import { histogramUserKey, loadFabHistogramView, saveFabHistogramView } from '../../utils/fabHistogramViewPrefs';
import { plotChartRow } from '../../utils/fabHistogramPlot';

const btn = {
  padding: '6px 12px',
  fontSize: '0.8rem',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  background: 'white',
  color: '#334155',
  cursor: 'pointer',
};

const inp = { ...btn, padding: '6px 8px', width: '100%', boxSizing: 'border-box' };

const seg = (on) => ({
  ...btn,
  background: on ? '#0f766e' : 'white',
  color: on ? 'white' : '#334155',
  borderColor: on ? '#0f766e' : '#d1d5db',
});

function fmtHours(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-CA', { maximumFractionDigits: 0 });
}

function setParam(qs, key, set) {
  if (set && set.size) qs.set(key, [...set].join(','));
}

function newCalRow() {
  return {
    _key: `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    label: '',
    shop: '',
    startDate: '',
    endDate: '',
    hoursPerWeek: 32,
  };
}

function newAbsRow(grain) {
  return { _key: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, grain, period: '', pct: 0 };
}

function setFromSaved(list, fallback) {
  if (Array.isArray(list)) return new Set(list.map(String));
  return fallback;
}

function columnFiltersFromSaved(raw) {
  const out = {};
  Object.entries(raw || {}).forEach(([key, values]) => {
    if (Array.isArray(values) && values.length) out[key] = new Set(values.map(String));
  });
  return out;
}

function pruneSelection(selected, options) {
  if (!selected || !selected.size) return selected;
  const allow = new Set(options || []);
  let changed = false;
  const next = new Set();
  selected.forEach((v) => {
    if (allow.has(v)) next.add(v);
    else changed = true;
  });
  return changed ? next : selected;
}

const DEFAULT_OPEN = {
  filters: true,
  kpis: true,
  source: false,
  planning: true,
  workbooks: false,
  table: false,
};

const ALL_CLOSED = {
  filters: false,
  kpis: false,
  source: false,
  planning: false,
  workbooks: false,
  table: false,
};

export default function FabricationHistogramPage() {
  const { instance, accounts } = useMsal();
  const { toast, showToast } = useToast(4500);
  const userKey = histogramUserKey(accounts?.[0]);
  const bootRef = useRef(null);
  if (bootRef.current === null) {
    bootRef.current = {
      key: userKey,
      prefs: userKey ? (loadFabHistogramView(userKey) || null) : null,
    };
  }
  const bootPrefs = bootRef.current.prefs || {};
  const fileRef = useRef(null);
  const printRef = useRef(null);
  const planningDirty = useRef(false);
  const colorTimer = useRef(null);
  const discInitRef = useRef({});
  const rsrcInitRef = useRef({});
  const hydratedKey = useRef(bootRef.current.key || '');
  if (Array.isArray(bootPrefs.disciplines)) discInitRef.current[bootPrefs.dataSource || 'p6'] = true;
  if (Array.isArray(bootPrefs.rsrcTypes)) rsrcInitRef.current[bootPrefs.dataSource || 'p6'] = true;

  const [data, setData] = useState(null);
  const [groupList, setGroupList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncingP6, setSyncingP6] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showPortfolios, setShowPortfolios] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(DEFAULT_OPEN);
  const [prefsReady, setPrefsReady] = useState(Boolean(bootRef.current.key));
  const [dataSource, setDataSource] = useState(bootPrefs.dataSource || 'p6');
  const [loadedAt, setLoadedAt] = useState(null);

  const [grain, setGrain] = useState(bootPrefs.grain || 'week');
  const [units, setUnits] = useState(bootPrefs.units || 'manweeks');
  const [groupBy, setGroupBy] = useState(bootPrefs.groupBy || 'workbook');
  const [splitBy, setSplitBy] = useState(bootPrefs.splitBy || 'none');
  const [showCapacity, setShowCapacity] = useState(bootPrefs.showCapacity !== false);
  const [showSCurve, setShowSCurve] = useState(!!bootPrefs.showSCurve);
  const [showMenLabels, setShowMenLabels] = useState(!!bootPrefs.showMenLabels);
  const [showBarTotals, setShowBarTotals] = useState(bootPrefs.showBarTotals !== false);
  const [hideCancelled, setHideCancelled] = useState(bootPrefs.hideCancelled !== false);
  const [shops, setShops] = useState(() => setFromSaved(bootPrefs.shops, new Set()));
  const [statuses, setStatuses] = useState(() => setFromSaved(bootPrefs.statuses, new Set()));
  const [projects, setProjects] = useState(() => setFromSaved(bootPrefs.projects, new Set()));
  const [disciplines, setDisciplines] = useState(() => setFromSaved(bootPrefs.disciplines, new Set()));
  const [resources, setResources] = useState(() => setFromSaved(bootPrefs.resources, new Set()));
  const [activities, setActivities] = useState(() => setFromSaved(bootPrefs.activities, new Set()));
  const [rsrcTypes, setRsrcTypes] = useState(() => setFromSaved(bootPrefs.rsrcTypes, new Set()));
  const [wbsNames, setWbsNames] = useState(() => setFromSaved(bootPrefs.wbsNames, new Set()));
  const [calendarNames, setCalendarNames] = useState(() => setFromSaved(bootPrefs.calendarNames, new Set()));
  const [portfolioIds, setPortfolioIds] = useState(() => setFromSaved(bootPrefs.portfolioIds, new Set()));
  const [programIds, setProgramIds] = useState(() => setFromSaved(bootPrefs.programIds, new Set()));
  const [xerClasses, setXerClasses] = useState(() => setFromSaved(bootPrefs.xerClasses, new Set(['current'])));
  const [horizon, setHorizon] = useState(bootPrefs.horizon || DEFAULT_WINDOW_HORIZON);
  const [dateFrom, setDateFrom] = useState(bootPrefs.dateFrom || '');
  const [dateTo, setDateTo] = useState(bootPrefs.dateTo || '');
  const [showFilters, setShowFilters] = useState(bootPrefs.showFilters !== false);
  const [slicerPanels, setSlicerPanels] = useState(() => (
    bootPrefs.slicerPanels && typeof bootPrefs.slicerPanels === 'object'
      ? { ...loadSlicerPanels(), ...bootPrefs.slicerPanels }
      : loadSlicerPanels()
  ));
  const [columnFilters, setColumnFilters] = useState(() => columnFiltersFromSaved(bootPrefs.columnFilters));

  const pinnedColumnIds = useMemo(() => Object.entries(slicerPanels || {})
    .filter(([id, on]) => on && isP6LegendId(id))
    .map(([id]) => id)
    .sort(), [slicerPanels]);

  const columnFilterKey = useMemo(() => {
    const out = {};
    pinnedColumnIds.forEach((id) => {
      const selected = columnFilters[id];
      if (selected && selected.size) out[id] = [...selected].sort();
    });
    return JSON.stringify(out);
  }, [pinnedColumnIds, columnFilters]);

  const [capDraft, setCapDraft] = useState([]);
  const [calDraft, setCalDraft] = useState([]);
  const [absDraft, setAbsDraft] = useState([]);
  const [colorDraft, setColorDraft] = useState({});
  const [defaultAbs, setDefaultAbs] = useState(8);
  const [hpwFallback, setHpwFallback] = useState(32);
  const [newShop, setNewShop] = useState('');
  const [newShopMen, setNewShopMen] = useState(0);

  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  const hydratePlanning = (payload) => {
    setCapDraft((payload.capacity || []).map((c, i) => ({
      ...c,
      _key: c.shop || `new-${i}`,
    })));
    setCalDraft((payload.calendars || []).map((c, i) => ({
      _key: `c-${c.id || i}`,
      id: c.id,
      label: c.label || '',
      shop: c.shop || '',
      startDate: c.startDate || '',
      endDate: c.endDate || '',
      hoursPerWeek: c.hoursPerWeek ?? 32,
    })));
    setAbsDraft((payload.absenteeism || []).map((a, i) => ({
      _key: `a-${a.id || i}`,
      id: a.id,
      grain: a.grain,
      period: a.period,
      pct: a.pct,
    })));
    setColorDraft({ ...(payload.seriesColors || {}) });
    if (payload.defaultAbsenteeism != null) setDefaultAbs(payload.defaultAbsenteeism);
    if (payload.hoursPerWeek != null) setHpwFallback(payload.hoursPerWeek);
  };

  const loadGroups = useCallback(async () => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', '/fab-histogram/portfolios');
      if (res?.data?.success && Array.isArray(res.data.portfolios)) {
        setGroupList(res.data.portfolios);
      }
    } catch {
      /* analytics still carries a copy when this list is empty */
    }
  }, [instance, accounts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ grain, groupBy, hideCancelled: String(hideCancelled), source: dataSource });
      if (splitBy && splitBy !== 'none') qs.set('splitBy', splitBy);
      setParam(qs, 'shops', shops);
      setParam(qs, 'statuses', statuses);
      setParam(qs, 'projects', projects);
      setParam(qs, 'resourceDisciplines', disciplines);
      setParam(qs, 'resources', resources);
      setParam(qs, 'activities', activities);
      setParam(qs, 'rsrcTypes', rsrcTypes);
      setParam(qs, 'wbsNames', wbsNames);
      setParam(qs, 'calendarNames', calendarNames);
      setParam(qs, 'portfolios', portfolioIds);
      setParam(qs, 'programs', programIds);
      setParam(qs, 'xerClasses', xerClasses);
      if (pinnedColumnIds.length) qs.set('columnSlicers', pinnedColumnIds.join(','));
      if (columnFilterKey !== '{}') qs.set('columnFilters', columnFilterKey);
      qs.set('horizon', horizon);
      if (dateFrom) qs.set('from', dateFrom);
      if (dateTo) qs.set('to', dateTo);
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/fab-histogram/analytics?${qs}`, null, { timeout: 120000 });
      if (res?.data?.success) {
        setData(res.data);
        setLoadedAt(new Date());
        if (!planningDirty.current) hydratePlanning(res.data);
      } else {
        showToast(res?.data?.message || 'Could not load histogram', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || err.message || 'Could not load histogram', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, grain, groupBy, splitBy, hideCancelled, shops, statuses, projects, disciplines, resources, activities, rsrcTypes, wbsNames, calendarNames, portfolioIds, programIds, xerClasses, pinnedColumnIds, columnFilterKey, horizon, dateFrom, dateTo, dataSource, showToast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  useEffect(() => {
    if (!userKey) return;
    if (hydratedKey.current === userKey) {
      setPrefsReady(true);
      return;
    }
    const prefs = loadFabHistogramView(userKey);
    hydratedKey.current = userKey;
    if (prefs) {
      if (prefs.dataSource) setDataSource(prefs.dataSource);
      if (prefs.grain) setGrain(prefs.grain);
      if (prefs.units) setUnits(prefs.units);
      if (prefs.groupBy) setGroupBy(prefs.groupBy);
      if (prefs.splitBy) setSplitBy(prefs.splitBy);
      if (typeof prefs.showCapacity === 'boolean') setShowCapacity(prefs.showCapacity);
      if (typeof prefs.showSCurve === 'boolean') setShowSCurve(prefs.showSCurve);
      if (typeof prefs.showMenLabels === 'boolean') setShowMenLabels(prefs.showMenLabels);
      if (typeof prefs.showBarTotals === 'boolean') setShowBarTotals(prefs.showBarTotals);
      if (typeof prefs.hideCancelled === 'boolean') setHideCancelled(prefs.hideCancelled);
      if (typeof prefs.showFilters === 'boolean') setShowFilters(prefs.showFilters);
      if (prefs.horizon) setHorizon(prefs.horizon);
      if (prefs.dateFrom !== undefined) setDateFrom(prefs.dateFrom || '');
      if (prefs.dateTo !== undefined) setDateTo(prefs.dateTo || '');
      if (Array.isArray(prefs.shops)) setShops(new Set(prefs.shops.map(String)));
      if (Array.isArray(prefs.statuses)) setStatuses(new Set(prefs.statuses.map(String)));
      if (Array.isArray(prefs.projects)) setProjects(new Set(prefs.projects.map(String)));
      if (Array.isArray(prefs.disciplines)) {
        setDisciplines(new Set(prefs.disciplines.map(String)));
        discInitRef.current[prefs.dataSource || 'p6'] = true;
      }
      if (Array.isArray(prefs.resources)) setResources(new Set(prefs.resources.map(String)));
      if (Array.isArray(prefs.activities)) setActivities(new Set(prefs.activities.map(String)));
      if (Array.isArray(prefs.rsrcTypes)) {
        setRsrcTypes(new Set(prefs.rsrcTypes.map(String)));
        rsrcInitRef.current[prefs.dataSource || 'p6'] = true;
      }
      if (Array.isArray(prefs.wbsNames)) setWbsNames(new Set(prefs.wbsNames.map(String)));
      if (Array.isArray(prefs.calendarNames)) setCalendarNames(new Set(prefs.calendarNames.map(String)));
      if (Array.isArray(prefs.portfolioIds)) setPortfolioIds(new Set(prefs.portfolioIds.map(String)));
      if (Array.isArray(prefs.programIds)) setProgramIds(new Set(prefs.programIds.map(String)));
      if (Array.isArray(prefs.xerClasses)) setXerClasses(new Set(prefs.xerClasses.map(String)));
      if (prefs.slicerPanels && typeof prefs.slicerPanels === 'object') {
        setSlicerPanels((prev) => ({ ...prev, ...prefs.slicerPanels }));
      }
      if (prefs.columnFilters && typeof prefs.columnFilters === 'object') {
        setColumnFilters(columnFiltersFromSaved(prefs.columnFilters));
      }
    }
    setPrefsReady(true);
  }, [userKey]);

  useEffect(() => {
    if (!prefsReady || !userKey) return undefined;
    const timer = setTimeout(() => {
      saveFabHistogramView(userKey, {
        dataSource,
        grain,
        units,
        groupBy,
        splitBy,
        showCapacity,
        showSCurve,
        showMenLabels,
        showBarTotals,
        hideCancelled,
        showFilters,
        shops: [...shops],
        statuses: [...statuses],
        projects: [...projects],
        disciplines: [...disciplines],
        resources: [...resources],
        activities: [...activities],
        rsrcTypes: [...rsrcTypes],
        wbsNames: [...wbsNames],
        calendarNames: [...calendarNames],
        portfolioIds: [...portfolioIds],
        programIds: [...programIds],
        xerClasses: [...xerClasses],
        horizon,
        dateFrom,
        dateTo,
        slicerPanels,
        columnFilters: Object.fromEntries(
          Object.entries(columnFilters).map(([key, values]) => [key, [...values]])
        ),
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [prefsReady, userKey, dataSource, grain, units, groupBy, splitBy, showCapacity, showSCurve, showMenLabels, showBarTotals, hideCancelled, showFilters, shops, statuses, projects, disciplines, resources, activities, rsrcTypes, wbsNames, calendarNames, portfolioIds, programIds, xerClasses, horizon, dateFrom, dateTo, slicerPanels, columnFilters]);

  useEffect(() => {
    if (data?.portfolios?.length) setGroupList(data.portfolios);
  }, [data?.portfolios]);

  useEffect(() => {
    const s = data?.slicers;
    if (!s) return;
    const named = (groupList.length ? groupList : data.portfolios || []);
    const allPrograms = named.filter((p) => p.layer !== 'portfolio');
    const allPortfolios = named.filter((p) => p.layer === 'portfolio');
    const programOpts = programsVisibleForPortfolios(allPrograms, allPortfolios, portfolioIds).map((p) => String(p.id));
    const portfolioOpts = allPortfolios.map((p) => String(p.id));
    setShops((prev) => pruneSelection(prev, s.shops));
    setStatuses((prev) => pruneSelection(prev, s.statuses));
    setProjects((prev) => pruneSelection(prev, s.projects));
    if (portfolioOpts.length) setPortfolioIds((prev) => pruneSelection(prev, portfolioOpts));
    if (programOpts.length) setProgramIds((prev) => pruneSelection(prev, programOpts));
    setResources((prev) => pruneSelection(prev, s.resources));
    setActivities((prev) => pruneSelection(prev, s.activities));
    setWbsNames((prev) => pruneSelection(prev, s.wbsNames));
    setCalendarNames((prev) => pruneSelection(prev, s.calendarNames));
    const columnOpts = s.columns || {};
    setColumnFilters((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(prev).forEach(([dim, selected]) => {
        const opts = columnOpts[dim];
        if (!Array.isArray(opts) || !opts.length) return;
        const pruned = pruneSelection(selected, opts);
        if (pruned !== selected) {
          next[dim] = pruned;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setDisciplines((prev) => {
      const opts = s.resourceDisciplines || [];
      const pruned = pruneSelection(prev, opts);
      if (!opts.length) return pruned;
      if (prev && prev.size) {
        discInitRef.current[dataSource] = true;
        return pruned;
      }
      if (discInitRef.current[dataSource]) return pruned;
      const fab = defaultResourceDisciplines(opts);
      return fab.length ? new Set(fab) : pruned;
    });
    setRsrcTypes((prev) => {
      const opts = s.rsrcTypes || [];
      const pruned = pruneSelection(prev, opts);
      if (!opts.length) return pruned;
      if (prev && prev.size) {
        rsrcInitRef.current[dataSource] = true;
        return pruned;
      }
      if (rsrcInitRef.current[dataSource]) return pruned;
      const labor = defaultLaborTypes(opts);
      return labor.length ? new Set(labor) : pruned;
    });
  }, [data?.slicers, data?.portfolios, dataSource, groupList, portfolioIds]);

  const markDirty = () => {
    planningDirty.current = true;
    setDirty(true);
  };

  const clearDirty = () => {
    planningDirty.current = false;
    setDirty(false);
  };

  const kpis = data?.kpis || {};
  const slicers = data?.slicers || {};
  const empty = !loading && !(data?.chart || []).length;
  const comingSoon = dataSource === 'cost_breakdown';
  const excelSource = dataSource === 'excel';
  const p6Source = dataSource === 'p6';
  const pageTitle = histogramTitle();
  const scopeChip = histogramScopeChip(disciplines);
  const fabOnly = isFabricationDisciplineSet(disciplines);
  const legendFields = useMemo(() => p6LegendFields(data?.legendFields), [data?.legendFields]);

  // A P6 legend key (UDF / activity code / resource code) only exists for the
  // copies that carry it. When the source or XER copies change and the key is
  // gone from the catalog, fall back so the chart is not one "(no value)" bar.
  useEffect(() => {
    if (!data) return;
    if (!p6Source) {
      if (isP6LegendId(groupBy)) setGroupBy('workbook');
      if (isP6LegendId(splitBy)) setSplitBy('none');
      return;
    }
    const known = new Set(legendFields.map((f) => f.id));
    if (!known.size) return;
    if (isP6LegendId(groupBy) && !known.has(groupBy)) setGroupBy('workbook');
    if (isP6LegendId(splitBy) && !known.has(splitBy)) setSplitBy('none');
  }, [data, p6Source, legendFields, groupBy, splitBy]);
  const groups = groupList.length ? groupList : (data?.portfolios || []);
  const programs = groups.filter((g) => g.layer !== 'portfolio');
  const portfolios = groups.filter((g) => g.layer === 'portfolio');
  const projectCatalog = data?.projectCatalog || [];

  const switchSource = (next) => {
    setDataSource(next);
    discInitRef.current[next] = false;
    rsrcInitRef.current[next] = false;
    setDisciplines(new Set());
    setRsrcTypes(new Set());
    setResources(new Set());
    setActivities(new Set());
    setWbsNames(new Set());
    setCalendarNames(new Set());
    setShops(new Set());
    setProjects(new Set());
    setStatuses(new Set());
    setPortfolioIds(new Set());
  };

  // Calendar shop options follow the Crew table (renames cascade to calendars on
  // save). Keep any shop an existing calendar row still points at so its current
  // selection stays visible until the next save remaps it.
  const shopChoices = useMemo(() => {
    const set = new Set(capDraft.map((c) => String(c.shop || '').trim()).filter(Boolean));
    calDraft.forEach((c) => {
      const s = String(c.shop || '').trim();
      if (s) set.add(s);
    });
    return [...set];
  }, [capDraft, calDraft]);

  const onUpload = async (fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/fab-histogram/import', form, { timeout: 120000 });
      if (res?.data?.success) {
        showToast(res.data.replacedAllExcel
          ? `${res.data.message}. Previous Excel dumps were replaced by this one file.`
          : res.data.message, 'success');
        if (res.data.errors?.length) {
          showToast(res.data.errors.map((e) => `${e.fileName}: ${e.message}`).join(' · '), 'error');
        }
        clearDirty();
        await load();
      } else {
        showToast(res?.data?.message || 'Import failed', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || err.message || 'Import failed', 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const exportWorkbook = async () => {
    setExporting(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', '/fab-histogram/export-edit', null, {
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = res?.data;
      if (!blob) throw new Error('Empty export');
      if (blob.type && String(blob.type).includes('json')) {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed.message || 'Export failed');
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const name = res.headers?.['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1]
        || exportFilename({ job: 'portal', reportSlug: 'Fabrication_Histogram_Edit', ext: 'xlsx' });
      a.href = url;
      a.download = decodeURIComponent(name);
      a.click();
      URL.revokeObjectURL(url);
      showToast('Workbook downloaded. Edit hours and Import that same file.', 'success');
    } catch (err) {
      showToast(err?.response?.data?.message || err.message || 'Could not export', 'error');
    } finally {
      setExporting(false);
    }
  };

  const syncP6 = async () => {
    setSyncingP6(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/fab-histogram/sync-p6', {}, { timeout: 180000 });
      if (res?.data?.success) {
        showToast(res.data.message, 'success');
        clearDirty();
        await load();
      } else {
        showToast(res?.data?.message || 'P6 had no remaining hours', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || err.message || 'Could not read P6', 'error');
    } finally {
      setSyncingP6(false);
    }
  };

  const deleteWorkbook = async (id) => {
    if (!window.confirm('Remove this workbook from the histogram?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/fab-histogram/workbooks/${id}`);
      showToast('Workbook removed', 'success');
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Delete failed', 'error');
    }
  };

  const addShopRow = () => {
    const shop = newShop.trim();
    if (!shop) return;
    if (capDraft.some((c) => String(c.shop || '').toLowerCase() === shop.toLowerCase())) {
      showToast('That shop is already listed', 'error');
      return;
    }
    markDirty();
    setCapDraft([...capDraft, { shop, men: Number(newShopMen) || 0, _key: `new-${Date.now()}` }]);
    setNewShop('');
    setNewShopMen(0);
  };

  const savePlanning = async () => {
    if (!calDraft.length) {
      showToast('Keep at least one calendar row (hours per week for a date range)', 'error');
      return;
    }
    if (capDraft.some((c) => !String(c.shop || '').trim())) {
      showToast('Every shop needs a name', 'error');
      return;
    }
    setSavingPlan(true);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', '/fab-histogram/settings', {
        hoursPerWeek: Number(hpwFallback),
        defaultAbsenteeism: Number(defaultAbs),
      });
      await authenticatedApiCall(instance, accounts, 'PUT', '/fab-histogram/capacity', {
        capacity: capDraft.map((c) => ({
          shop: String(c.shop || '').trim(),
          men: Number(c.men),
          previousShop: c._key && !String(c._key).startsWith('new-') ? c._key : null,
        })),
      });
      await authenticatedApiCall(instance, accounts, 'PUT', '/fab-histogram/calendars', {
        calendars: calDraft.map((c) => ({
          label: c.label,
          shop: c.shop || null,
          startDate: c.startDate,
          endDate: c.endDate || null,
          hoursPerWeek: Number(c.hoursPerWeek),
        })),
      });
      await authenticatedApiCall(instance, accounts, 'PUT', '/fab-histogram/absenteeism', {
        absenteeism: absDraft.filter((a) => a.period).map((a) => ({
          grain: a.grain,
          period: a.period,
          pct: Number(a.pct),
        })),
      });
      clearDirty();
      showToast('Planning saved — capacity line and man-weeks updated', 'success');
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSavingPlan(false);
    }
  };

  const persistColors = (next) => {
    if (colorTimer.current) clearTimeout(colorTimer.current);
    colorTimer.current = setTimeout(async () => {
      try {
        await authenticatedApiCall(instance, accounts, 'PUT', '/fab-histogram/colors', { seriesColors: next });
      } catch (err) {
        showToast(err?.response?.data?.message || 'Could not save colours', 'error');
      }
    }, 400);
  };

  const onColorChange = (name, hex) => {
    const next = { ...colorDraft, [name]: hex };
    setColorDraft(next);
    persistColors(next);
  };

  const downloadCsv = () => {
    const rows = data?.chart || [];
    const series = data?.series || [];
    const headers = ['Period', 'Total', 'Capacity', 'Cumulative hours', ...series.map((s) => stripDumpSuffix(s) || s)];
    const lines = [headers.join(',')];
    rows.forEach((r) => {
      const plotted = plotChartRow(r, series, units);
      lines.push([
        r.label,
        Math.round(plotted._total),
        Math.round(plotted._cap),
        Math.round(r.cumulative),
        ...series.map((s) => Math.round(plotted[s] || 0)),
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename({ job: 'portal', reportSlug: 'Fabrication_Histogram', ext: 'csv' });
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    if (!printRef.current || pdfBusy) return;
    const wasOpen = open;
    setOpen(ALL_CLOSED);
    setPdfBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 80));
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);
      const canvas = await html2canvas(printRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, omitJobLine: true, margin: 10 });
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(pageTitle, 10, letterheadY);
      const printed = new Date().toLocaleString('en-CA', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      });
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Printed: ${printed}`, pageW - 10, letterheadY, { align: 'right' });
      const top = letterheadY + 6;
      const maxW = pageW - 16;
      const maxH = pageH - top - 8;
      let drawW = maxW;
      let drawH = (canvas.height * drawW) / canvas.width;
      if (drawH > maxH) {
        drawH = maxH;
        drawW = (canvas.width * drawH) / canvas.height;
      }
      doc.addImage(imgData, 'PNG', 8, top, drawW, drawH);
      doc.save(exportFilename({ job: 'portal', reportSlug: 'Fabrication_Histogram', ext: 'pdf' }));
    } catch (err) {
      console.error('PDF export failed:', err);
      showToast('PDF export failed', 'error');
    } finally {
      setPdfBusy(false);
      setOpen(wasOpen);
    }
  };

  const lastImport = useMemo(() => {
    if (!kpis.lastImport) return '—';
    try {
      return new Date(kpis.lastImport).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return String(kpis.lastImport);
    }
  }, [kpis.lastImport]);

  const loadedLabel = useMemo(() => {
    if (!loadedAt) return '—';
    return loadedAt.toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });
  }, [loadedAt]);

  const monthAbs = absDraft.filter((a) => a.grain === 'month');
  const weekAbs = absDraft.filter((a) => a.grain === 'week');

  return (
    <div style={{ padding: '1rem 1.25rem 5rem', maxWidth: '100%', margin: 0 }}>
      {toast.show && (
        <div style={{
          position: 'fixed', top: 20, right: 20, padding: '0.85rem 1.15rem',
          backgroundColor: toast.type === 'success' ? '#0f766e' : '#be123c',
          color: 'white', borderRadius: 8, zIndex: 9999, maxWidth: 420, fontSize: '0.85rem',
        }}>
          {toast.message}
        </div>
      )}
      {showHelp && (
        <FabricationHistogramHelpModal
          onClose={() => setShowHelp(false)}
          loadedLabel={loadedLabel}
          lastImport={lastImport}
          workbookCount={kpis.workbookCount || 0}
        />
      )}
      {showPortfolios && (
        <FabricationHistogramPortfoliosModal
          onClose={() => setShowPortfolios(false)}
          portfolios={groups}
          catalog={projectCatalog}
          dataSource={dataSource}
          onChanged={async () => { await loadGroups(); await load(); }}
        />
      )}
      {showAudit && (
        <FabricationHistogramAuditModal
          isOpen={showAudit}
          onClose={() => setShowAudit(false)}
          onReverted={() => {
            clearDirty();
            load();
          }}
          onNavigate={(entry) => {
            const t = entry?.entity_type;
            const action = entry?.action;
            if (t === 'workbook' || action === 'import' || action === 'delete' || action === 'export' || action === 'sync') {
              setOpen((o) => ({ ...o, workbooks: true }));
            } else if (t === 'p6' || action === 'refresh') {
              setShowFilters(true);
              switchSource('p6');
            } else if (['capacity', 'settings', 'calendars', 'absenteeism'].includes(t)) {
              setOpen((o) => ({ ...o, planning: true }));
            }
            setShowAudit(false);
          }}
        />
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        multiple
        hidden
        onChange={(e) => onUpload(e.target.files)}
      />

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: '0.75rem',
      }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {pageTitle}
        </h2>
        {scopeChip && (
          <span
            title="Resource discipline filter — clear it in Filters to see every trade"
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#0f766e',
              background: '#ccfbf1',
              border: '1px solid #99f6e4',
              borderRadius: 999,
              padding: '2px 8px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {scopeChip}
          </span>
        )}
        <div style={{
          display: 'flex',
          minWidth: 0,
          flex: 1,
          overflowX: 'auto',
        }}>
          <div aria-hidden="true" style={{ flex: 1, minWidth: 0 }} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap', flexShrink: 0 }}>
          <button type="button" style={seg(showFilters)} onClick={() => setShowFilters((v) => !v)}>
            {showFilters ? '▾ Filters' : '▸ Filters'}
          </button>
          <button type="button" style={seg(open.kpis)} onClick={() => toggle('kpis')}>KPIs</button>
          <FilterDropdown
            label="Source"
            summary={p6Source ? 'P6' : excelSource ? 'Excel' : 'CB'}
            buttonWidth={92}
            popoverWidth={220}
            portal
            zIndex={200}
            active
          >
            <FilterRadioList
              name="fab-source-header"
              search={false}
              value={dataSource}
              onChange={switchSource}
              groups={[{ items: [
                { id: 'p6', label: 'Primavera P6' },
                { id: 'excel', label: 'Excel data dumps' },
                { id: 'cost_breakdown', label: 'Portal cost breakdown' },
              ] }]}
            />
          </FilterDropdown>
          <button type="button" style={seg(open.planning)} onClick={() => toggle('planning')}>Planning</button>
          <button type="button" style={seg(open.workbooks)} onClick={() => toggle('workbooks')}>
            Workbooks ({kpis.workbookCount || 0})
          </button>
          <button type="button" style={seg(open.table)} onClick={() => toggle('table')} disabled={empty || comingSoon}>
            Period table
          </button>
          <ListenButton compact guideId={FAB_HIST_GUIDE_ID} title="Corporation Resource Histogram Help" accent="#0f766e" />
          <button type="button" style={btn} onClick={() => setShowHelp(true)}>❓ Help</button>
          <button type="button" style={btn} onClick={() => setShowAudit(true)} title="View import, planning, and refresh history">
            📋 Audit Log
          </button>
          {excelSource && (
            <>
              <button type="button" style={btn} onClick={() => fileRef.current?.click()} disabled={uploading || comingSoon}>
                {uploading ? 'Importing…' : 'Import'}
              </button>
              <button type="button" style={btn} onClick={exportWorkbook} disabled={exporting || empty || comingSoon}>
                {exporting ? 'Exporting…' : 'Export'}
              </button>
            </>
          )}
          {p6Source && (
            <button type="button" style={{ ...btn, background: '#0f766e', color: 'white', borderColor: '#0f766e' }} onClick={syncP6} disabled={syncingP6}>
              {syncingP6 ? 'Refreshing…' : 'Refresh'}
            </button>
          )}
          <button type="button" style={btn} onClick={downloadCsv} disabled={empty || comingSoon}>↓ CSV</button>
          <button type="button" style={btn} onClick={downloadPdf} disabled={empty || comingSoon || pdfBusy}>
            {pdfBusy ? 'PDF…' : '↓ PDF'}
          </button>
          </div>
        </div>
      </div>

      {open.kpis && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: '0.75rem' }}>
          <Kpi label="Total hours" value={fmtHours(kpis.totalHours)} sub={`${fmtHours(kpis.activeHours)} active · ${fmtHours(kpis.forecastedHours)} forecasted`} />
          <Kpi label="Peak period" value={kpis.peakPeriod ? kpis.peakPeriod : '—'} sub={kpis.peakHours ? `${fmtHours(kpis.peakHours)} h · ${Math.round(kpis.peakMen || 0)} men` : 'No hours yet'} />
          <Kpi label="Over capacity" value={String(kpis.overCapacityPeriods || 0)} sub="vs crew × calendar × (1 − absentees)" warn={kpis.overCapacityPeriods > 0} />
          <Kpi label="Workbooks" value={String(kpis.workbookCount || 0)} sub={`Last import ${lastImport}`} />
        </div>
      )}

      {!fabOnly && showCapacity && (
        <div style={{ fontSize: '0.75rem', color: '#475569', marginBottom: 8 }}>
          Shop capacity is fab-shop crew only — turn the line off, or filter Resource discipline to Fabrication, to compare crew against shop load.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', position: 'relative' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
      <div ref={printRef} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.75rem 0.75rem 1rem', minHeight: 460 }}>
        {comingSoon && (
          <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: '#475569' }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Portal cost breakdown — coming soon</div>
            <p style={{ margin: '0 auto 1rem', maxWidth: 560, lineHeight: 1.55, fontSize: '0.9rem' }}>
              This option will read shop hours from Estimation cost breakdowns in the portal so a bid change updates the load without a side spreadsheet.
              It is not built yet. Switch back to Primavera P6 for the live histogram.
            </p>
            <button type="button" style={{ ...btn, background: '#0f766e', color: 'white', borderColor: '#0f766e' }} onClick={() => switchSource('p6')}>
              Use Primavera P6
            </button>
          </div>
        )}
        {!comingSoon && loading && <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading shop hours…</div>}
        {!comingSoon && !loading && empty && excelSource && (
          <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: '#475569' }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>No fabrication hours yet</div>
            <p style={{ margin: '0 auto 1rem', maxWidth: 520, lineHeight: 1.55, fontSize: '0.9rem' }}>
              Import the Data Dump workbooks — Sheet1, Remaining Units, Sunday weeks. The chart reloads when the upload finishes.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button type="button" style={{ ...btn, background: '#0f766e', color: 'white', borderColor: '#0f766e', padding: '8px 16px' }} onClick={() => fileRef.current?.click()}>
                Import
              </button>
            </div>
          </div>
        )}
        {!comingSoon && !loading && empty && p6Source && (
          <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: '#475569' }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>No P6 remaining hours yet</div>
            <p style={{ margin: '0 auto 1rem', maxWidth: 560, lineHeight: 1.55, fontSize: '0.9rem' }}>
              Refresh pulls Current and dated weeklies. Use the XER copy slicer to keep Current, the latest dated weekly, or every XER. Resource type starts on Labor. Remaining is split evenly by week.
            </p>
            <button type="button" style={{ ...btn, background: '#0f766e', color: 'white', borderColor: '#0f766e', padding: '8px 16px' }} onClick={syncP6} disabled={syncingP6}>
              {syncingP6 ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        )}
        {!comingSoon && !loading && !empty && (
          <FabricationHistogramChart
            chart={data.chart}
            series={data.series}
            seriesMeta={data.seriesMeta || {}}
            units={units}
            grain={grain}
            showCapacity={showCapacity}
            showSCurve={showSCurve}
            showMenLabels={showMenLabels}
            showBarTotals={showBarTotals}
            seriesColors={colorDraft}
            hideColorPickers={pdfBusy}
            onColorChange={onColorChange}
          />
        )}
      </div>

      {open.planning && (
        <div style={{ marginTop: 12, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>Planning</h3>
            <span style={{ fontSize: 12, color: '#64748b' }} title="P6 source: men on a bar = hours ÷ the Hours/Week of the calendar the activity runs on (5x10 → 50 h, 6x10 → 60 h). Planning calendars and absenteeism do not change those bars or the capacity line. On Excel dumps the line is crew × h/wk × (1 − absenteeism).">
              On P6, capacity = crew × h/wk and absenteeism is off. On Excel dumps the line is crew × h/wk × (1 − abs). P6 bars are P6’s own men.
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {dirty && (
                <span style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#b45309',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: 999,
                  padding: '2px 8px',
                  whiteSpace: 'nowrap',
                }}>
                  Unsaved changes
                </span>
              )}
              <button
                type="button"
                style={{
                  ...btn,
                  background: dirty ? '#b45309' : '#0f766e',
                  color: 'white',
                  borderColor: dirty ? '#b45309' : '#0f766e',
                }}
                onClick={savePlanning}
                disabled={savingPlan}
              >
                {savingPlan ? 'Saving…' : 'Save planning'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 20 }}>
            <PlanCol title="Crew" hint="Rename remaps hours on save." style={{ flex: '1 1 210px', maxWidth: 280 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: 60 }} />
                  <col style={{ width: 42 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={planTh}>Shop</th>
                    <th style={planTh}>Men</th>
                    <th style={planTh} />
                  </tr>
                </thead>
                <tbody>
                  {capDraft.map((row, i) => (
                    <tr key={row._key || i}>
                      <td style={planTd}>
                        <input
                          value={row.shop}
                          onChange={(e) => {
                            markDirty();
                            const next = [...capDraft];
                            next[i] = { ...row, shop: e.target.value };
                            setCapDraft(next);
                          }}
                          style={{ ...planInp, width: '100%' }}
                        />
                      </td>
                      <td style={planTd}>
                        <input
                          type="number"
                          min={0}
                          step={0.5}
                          value={row.men}
                          onChange={(e) => {
                            markDirty();
                            const next = [...capDraft];
                            next[i] = { ...row, men: e.target.value };
                            setCapDraft(next);
                          }}
                          style={{ ...planInp, width: '100%' }}
                        />
                      </td>
                      <td style={planTd}>
                        <button
                          type="button"
                          title="Remove shop"
                          style={iconBtn}
                          onClick={() => {
                            if (capDraft.length <= 1) {
                              showToast('Keep at least one shop', 'error');
                              return;
                            }
                            markDirty();
                            setCapDraft(capDraft.filter((_, idx) => idx !== i));
                          }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={planTd}>
                      <input
                        placeholder="Add shop"
                        value={newShop}
                        onChange={(e) => setNewShop(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addShopRow(); }}
                        style={{ ...planInp, width: '100%' }}
                      />
                    </td>
                    <td style={planTd}>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={newShopMen}
                        onChange={(e) => setNewShopMen(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addShopRow(); }}
                        style={{ ...planInp, width: '100%' }}
                      />
                    </td>
                    <td style={planTd}>
                      <button type="button" style={{ ...btn, padding: '3px 6px', fontSize: '0.75rem' }} onClick={addShopRow}>Add</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </PlanCol>

            <PlanCol
              title="Calendar"
              hint="All shops, or one floor. Later start date wins."
              style={{ flex: '2 1 420px', minWidth: 0 }}
              extra={(
                <label style={{ fontSize: 11, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  Fallback h/wk
                  <input
                    type="number"
                    min={1}
                    max={80}
                    value={hpwFallback}
                    onChange={(e) => { markDirty(); setHpwFallback(e.target.value); }}
                    style={{ ...planInp, width: 52 }}
                  />
                </label>
              )}
              action={(
                <button type="button" style={linkBtn} onClick={() => { markDirty(); setCalDraft([...calDraft, newCalRow()]); }}>
                  + Add
                </button>
              )}
            >
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '23%' }} />
                    <col style={{ width: '23%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: 28 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={planTh}>Shop</th>
                      <th style={planTh}>Label</th>
                      <th style={planTh}>Start</th>
                      <th style={planTh}>End</th>
                      <th style={planTh}>h/wk</th>
                      <th style={planTh} />
                    </tr>
                  </thead>
                  <tbody>
                    {calDraft.map((row) => (
                      <tr key={row._key}>
                        <td style={planTd}>
                          <select
                            value={row.shop || ''}
                            onChange={(e) => {
                              markDirty();
                              setCalDraft(calDraft.map((c) => (c._key === row._key ? { ...c, shop: e.target.value } : c)));
                            }}
                            style={{ ...planInp, width: '100%' }}
                          >
                            <option value="">All shops</option>
                            {shopChoices.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td style={planTd}>
                          <input
                            placeholder="Optional"
                            value={row.label}
                            onChange={(e) => {
                              markDirty();
                              setCalDraft(calDraft.map((c) => (c._key === row._key ? { ...c, label: e.target.value } : c)));
                            }}
                            style={{ ...planInp, width: '100%' }}
                          />
                        </td>
                        <td style={planTd}>
                          <input
                            type="date"
                            value={row.startDate || ''}
                            onChange={(e) => {
                              markDirty();
                              setCalDraft(calDraft.map((c) => (c._key === row._key ? { ...c, startDate: e.target.value } : c)));
                            }}
                            style={{ ...planInp, width: '100%' }}
                          />
                        </td>
                        <td style={planTd}>
                          <input
                            type="date"
                            value={row.endDate || ''}
                            onChange={(e) => {
                              markDirty();
                              setCalDraft(calDraft.map((c) => (c._key === row._key ? { ...c, endDate: e.target.value } : c)));
                            }}
                            style={{ ...planInp, width: '100%' }}
                          />
                        </td>
                        <td style={planTd}>
                          <input
                            type="number"
                            min={1}
                            max={80}
                            value={row.hoursPerWeek}
                            onChange={(e) => {
                              markDirty();
                              setCalDraft(calDraft.map((c) => (c._key === row._key ? { ...c, hoursPerWeek: e.target.value } : c)));
                            }}
                            style={{ ...planInp, width: '100%' }}
                          />
                        </td>
                        <td style={planTd}>
                          <button
                            type="button"
                            title="Remove calendar"
                            style={iconBtn}
                            onClick={() => {
                              if (calDraft.length <= 1) return;
                              markDirty();
                              setCalDraft(calDraft.filter((c) => c._key !== row._key));
                            }}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </PlanCol>

            <PlanCol
              title="Absenteeism"
              hint="Weeks beat months. Blank rows ignored. A week row covers the whole week containing that date."
              style={{ flex: '1 1 300px', maxWidth: 560 }}
              extra={(
                <label style={{ fontSize: 11, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  Default %
                  <input
                    type="number"
                    min={0}
                    max={95}
                    step={0.5}
                    value={defaultAbs}
                    onChange={(e) => { markDirty(); setDefaultAbs(e.target.value); }}
                    style={{ ...planInp, width: 52 }}
                  />
                </label>
              )}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(225px, 1fr))', gap: 10 }}>
                <AbsTable
                  title="Months"
                  type="month"
                  rows={monthAbs}
                  onChange={(row, next) => {
                    markDirty();
                    setAbsDraft(absDraft.map((a) => (a._key === row._key ? next : a)));
                  }}
                  onRemove={(row) => {
                    markDirty();
                    setAbsDraft(absDraft.filter((a) => a._key !== row._key));
                  }}
                  onAdd={() => { markDirty(); setAbsDraft([...absDraft, newAbsRow('month')]); }}
                />
                <AbsTable
                  title="Weeks"
                  type="week"
                  rows={weekAbs}
                  onChange={(row, next) => {
                    markDirty();
                    setAbsDraft(absDraft.map((a) => (a._key === row._key ? next : a)));
                  }}
                  onRemove={(row) => {
                    markDirty();
                    setAbsDraft(absDraft.filter((a) => a._key !== row._key));
                  }}
                  onAdd={() => { markDirty(); setAbsDraft([...absDraft, newAbsRow('week')]); }}
                />
              </div>
            </PlanCol>
          </div>
        </div>
      )}

      {open.table && !empty && !comingSoon && (
        <div style={{ overflowX: 'auto', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 12 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.78rem' }}>
            <thead>
              <tr>
                {['Period', 'Total', 'Capacity', 'Variance', ...(data.series || []).map((s) => stripDumpSuffix(s) || s)].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.chart || []).map((row) => {
                const plotted = plotChartRow(row, data.series || [], units);
                const total = plotted._total;
                const cap = plotted._cap;
                const over = total - cap;
                return (
                  <tr key={row.period}>
                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9' }}>{row.label}</td>
                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9' }}>{fmtHours(total)}</td>
                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9' }}>{fmtHours(cap)}</td>
                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9', color: over > 0.5 ? '#be123c' : '#0f766e' }}>
                      {over > 0 ? `+${fmtHours(over)}` : fmtHours(over)}
                    </td>
                    {(data.series || []).map((s, i) => {
                      const shown = Math.round(plotted[s] || 0);
                      return (
                        <td key={s} style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9', color: seriesColor(s, i, colorDraft) }}>
                          {shown ? fmtHours(shown) : ''}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open.workbooks && (
        <div style={{ marginTop: 12, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.85rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>Workbooks</h3>
            <button type="button" onClick={() => toggle('workbooks')} style={{ ...btn, border: 'none' }}>Close</button>
          </div>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#475569', lineHeight: 1.5 }}>
            {excelSource
              ? 'Import one or many Data Dump workbooks — the chart reloads when the upload finishes. Export is one file — edit hours and Import it back. That import wipes every Excel dump. A single-job Import still only replaces that job.'
              : p6Source
                ? 'These rows came from Primavera P6 (latest dated weekly per estimate). Refresh replaces the P6 stack. Excel dumps stay on the Excel source.'
                : 'No workbooks on this source yet.'}
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {excelSource && (
              <>
                <button type="button" style={btn} onClick={() => fileRef.current?.click()}>Import</button>
                <button type="button" style={btn} onClick={exportWorkbook} disabled={exporting || empty}>
                  {exporting ? 'Exporting…' : 'Export'}
                </button>
              </>
            )}
            {p6Source && (
              <button type="button" style={{ ...btn, background: '#0f766e', color: 'white', borderColor: '#0f766e' }} onClick={syncP6} disabled={syncingP6}>
                {syncingP6 ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  {['File name', 'Project (Excel)', 'Estimate', 'Job', 'Hours', 'Weeks', 'Imported', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.workbooks || []).map((w) => (
                  <tr key={w.id}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>{String(w.file_name || '').replace(/\.(xlsx|xls)$/i, '')}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>{w.project_label || '—'}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>{w.estimate_code || '—'}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>{w.job_number || '—'}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>{fmtHours(w.hour_total)}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>{w.week_first} → {w.week_last}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>{w.imported_by_name || w.imported_by || '—'}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>
                      <button type="button" style={{ ...btn, color: '#be123c', borderColor: '#fecdd3' }} onClick={() => deleteWorkbook(w.id)}>Remove</button>
                    </td>
                  </tr>
                ))}
                {!(data?.workbooks || []).length && (
                  <tr><td colSpan={8} style={{ padding: 16, color: '#94a3b8' }}>No workbooks yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
      <FabricationHistogramFilterPane
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onOpen={() => setShowFilters(true)}
        p6Source={p6Source}
        dataSource={dataSource}
        onSource={switchSource}
        grain={grain}
        setGrain={setGrain}
        units={units}
        setUnits={setUnits}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        splitBy={splitBy}
        setSplitBy={setSplitBy}
        legendFields={legendFields}
        showCapacity={showCapacity}
        setShowCapacity={setShowCapacity}
        showSCurve={showSCurve}
        setShowSCurve={setShowSCurve}
        showBarTotals={showBarTotals}
        setShowBarTotals={setShowBarTotals}
        showMenLabels={showMenLabels}
        setShowMenLabels={setShowMenLabels}
        hideCancelled={hideCancelled}
        setHideCancelled={setHideCancelled}
        xerClasses={xerClasses}
        setXerClasses={setXerClasses}
        xerInventory={data?.xerInventory || null}
        projectLabels={data?.projectLabels || null}
        horizon={horizon}
        setHorizon={setHorizon}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        slicers={slicers}
        portfolios={portfolios}
        programs={programs}
        shops={shops}
        setShops={setShops}
        statuses={statuses}
        setStatuses={setStatuses}
        projects={projects}
        setProjects={setProjects}
        disciplines={disciplines}
        setDisciplines={(next) => {
          discInitRef.current[dataSource] = true;
          setDisciplines(next);
        }}
        resources={resources}
        setResources={setResources}
        activities={activities}
        setActivities={setActivities}
        rsrcTypes={rsrcTypes}
        setRsrcTypes={(next) => {
          rsrcInitRef.current[dataSource] = true;
          setRsrcTypes(next);
        }}
        wbsNames={wbsNames}
        setWbsNames={setWbsNames}
        calendarNames={calendarNames}
        setCalendarNames={setCalendarNames}
        portfolioIds={portfolioIds}
        setPortfolioIds={setPortfolioIds}
        programIds={programIds}
        setProgramIds={setProgramIds}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        onDefinePortfolios={() => setShowPortfolios(true)}
        slicerPanels={slicerPanels}
        setSlicerPanels={setSlicerPanels}
      />
      </div>

    </div>
  );
}

const th = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' };
const td = { padding: '6px 8px', borderBottom: '1px solid #f1f5f9' };

const planTh = { ...th, padding: '4px 6px', fontSize: 11, color: '#64748b', fontWeight: 700 };
const planTd = { ...td, padding: '3px 4px' };
const planInp = { ...inp, padding: '4px 6px', fontSize: '0.78rem' };

const iconBtn = {
  ...btn,
  padding: '4px 8px',
  color: '#be123c',
  borderColor: '#fecdd3',
  lineHeight: 1,
};

const linkBtn = {
  border: 'none',
  background: 'none',
  color: '#0f766e',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  padding: 0,
};

function PlanCol({ title, hint, action, extra, children, style }) {
  return (
    <section style={{ minWidth: 0, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {extra}
          {action}
        </div>
      </div>
      {hint && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, lineHeight: 1.35 }}>{hint}</div>}
      {children}
    </section>
  );
}

function AbsTable({ title, type, rows, onChange, onRemove, onAdd }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>{title}</span>
        <button type="button" style={linkBtn} onClick={onAdd}>+ Add</button>
      </div>
      {rows.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: 'fixed' }}>
          <colgroup>
            <col />
            <col style={{ width: 56 }} />
            <col style={{ width: 34 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={planTh}>{type === 'month' ? 'Month' : 'Week of'}</th>
              <th style={planTh}>%</th>
              <th style={planTh} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._key}>
                <td style={planTd}>
                  <input
                    type={type === 'month' ? 'month' : 'date'}
                    value={row.period || ''}
                    onChange={(e) => onChange(row, { ...row, period: e.target.value })}
                    style={{ ...planInp, width: '100%', minWidth: 0 }}
                  />
                </td>
                <td style={planTd}>
                  <input
                    type="number"
                    min={0}
                    max={95}
                    step={0.5}
                    value={row.pct}
                    onChange={(e) => onChange(row, { ...row, pct: e.target.value })}
                    style={{ ...planInp, width: '100%' }}
                  />
                </td>
                <td style={planTd}>
                  <button type="button" title="Remove" style={iconBtn} onClick={() => onRemove(row)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ fontSize: 11, color: '#94a3b8' }}>None — default applies</div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, warn }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.7rem 0.85rem' }}>
      <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: warn ? '#be123c' : '#0f172a', marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>{sub}</div>
    </div>
  );
}
